import { FC, useMemo } from 'react';
import { Tag } from '@carbon/react';
import { Pagination } from 'components/pagination';
import { ColumnDef } from '@tanstack/react-table';
import { useAtom, useAtomValue } from 'jotai';
import { DataTable } from 'components/data-table';
import { FormattedCount } from 'components/formatted-count';
import { subDocsAtomFamily, subDocsFilterAtomFamily } from './atoms';
import { extractionStatusColors, type ProvisioningDocument } from './types';
import { DOCUMENT_LINK_COLUMNS } from './document-columns';
import styles from './page.module.scss';

interface BatchDocumentsTableProps {
  batchId: number;
  submissionId: number;
}

export const BatchDocumentsTable: FC<BatchDocumentsTableProps> = ({
  batchId,
  submissionId,
}) => {
  const key = `${batchId}-${submissionId}`;
  const docsLoadable = useAtomValue(
    subDocsAtomFamily({ batchId, submissionId })
  );
  const [filters, setFilters] = useAtom(subDocsFilterAtomFamily(key));

  const columns = useMemo<ColumnDef<ProvisioningDocument>[]>(
    () => [
      ...DOCUMENT_LINK_COLUMNS,
      {
        id: 'extractionStatus',
        accessorKey: 'extractionStatus',
        header: 'Extraction',
        size: 110,
        cell: (info) => {
          const status = info.getValue() as string;
          return (
            <Tag type={extractionStatusColors[status] || 'gray'} size="sm">
              {status}
            </Tag>
          );
        },
      },
      {
        id: 'completionStatus',
        accessorKey: 'completionStatus',
        header: 'Completion',
        size: 110,
        cell: (info) => {
          const status = info.getValue() as string;
          if (!status) return <span className={styles.muted}>--</span>;
          return (
            <Tag type={extractionStatusColors[status] || 'gray'} size="sm">
              {status}
            </Tag>
          );
        },
      },
      {
        id: 'entitiesCreated',
        accessorKey: 'entitiesCreated',
        header: 'Entities',
        size: 100,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'locationsCreated',
        accessorKey: 'locationsCreated',
        header: 'Locations',
        size: 105,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'addressesCreated',
        accessorKey: 'addressesCreated',
        header: 'Addresses',
        size: 105,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'capacitiesCreated',
        accessorKey: 'capacitiesCreated',
        header: 'Capacities',
        size: 110,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'citationsCreated',
        accessorKey: 'citationsCreated',
        header: 'Citations',
        size: 100,
        meta: { type: 'integer' },
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'errorMessage',
        accessorKey: 'errorMessage',
        header: 'Error',
        size: 300,
        cell: (info) => {
          const msg = info.getValue() as string;
          if (!msg) return <span className={styles.muted}>&#8212;</span>;
          return (
            <span className={styles.errorTextWrap} title={msg}>
              {msg}
            </span>
          );
        },
      },
    ],
    []
  );

  const documents = useMemo(() => {
    if (docsLoadable.state !== 'hasData' || !docsLoadable.data) return [];
    return docsLoadable.data.documents || [];
  }, [docsLoadable]);

  const isLoading = docsLoadable.state === 'loading';

  return (
    <div className={styles.expandedRowWrapper}>
      <DataTable
        data={documents}
        columns={columns}
        getRowId={(row) => String(row.documentId)}
        isLoading={isLoading}
        persistenceKey={`provisioning-sub-docs-${submissionId}`}
        config={{
          columnResizing: true,
          columnReordering: false,
          columnPinning: false,
          sorting: true,
          columnVisibility: true,
          globalSearch: false,
          columnFilters: false,
          hideFooter: true,
        }}
      />
      <Pagination
        page={filters.page}
        pageSize={filters.perPage}
        pageSizes={[10, 25, 50]}
        totalItems={
          docsLoadable.state === 'hasData'
            ? (docsLoadable.data?.pagination.totalCount ?? 0)
            : 0
        }
        onChange={({ page, pageSize }: { page: number; pageSize: number }) => {
          setFilters({ page, perPage: pageSize });
        }}
      />
    </div>
  );
};
